'use client'

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNextButton,
  CarouselPreviousButton,
} from '@lastbrand/web-ui/carousel'
import classnames from 'classnames'

import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { useWidget } from '@src/components/pl-base-widget/contexts/page-widget-store'
import StyliticsRecommendationPl from '@src/components/widgets/stylitics-recommendation'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { useFreeDragCarousel } from '@src/ds/blocks/carousel-plugins'
import {
  matchesPlWidgetId,
  useStatsigPlWidgetId,
} from '@src/experiments/statsig-integration/use-statsig-pl-widget-ids-config.hook'
import { LoaderWrapper as SvgLoaderWrapper } from '@src/quince-components/src'
import LoaderWrapper from '@src/quince-components/src/atoms/loaderWrapper/LoaderWrapper'
import Swiper from '@src/quince-components/src/atoms/swiper'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import pairStyles from '@src/quince-site-pdp/src/organisms/productPageSections/pairWithProducts/pairWithProductsV2.module.scss'
import { SIMILAR_PRODUCTS_SECTION_NAME_FOR_REF } from '@src/quince-site-pdp/src/organisms/productPageSections/similarProducts/constant'
import { useStyliticsExperiment } from '@src/quince-site-pdp/src/templates/productPage/hooks/use-stylitics-experiment'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { CardDisplayOptionsProvider } from '../single-product-card/card-display-options-context'
import { CardTrackingProvider } from '../single-product-card/card-tracking-context'
import { CAROUSEL_ARROW_DIRECTION, useRecoBandTelemetry } from '../use-reco-band-telemetry'

import {
  carouselViewportCss,
  carouselWrapperClass,
  headerRowClass,
  headingClass,
  loaderWrapperClass,
  navGroupClass,
  sectionClass,
} from './product-reco-pdp.styles'

import type { HorizontalProductListWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

/**
 * PDP horizontal-product-list (recommendations) container.
 *
 * DS path  — Figma "Shop the Look" band (nodes 6228:42330 / 6236:52337): an uppercase grosa eyebrow
 *             title + desktop nav arrows, a 1504-capped 4-up row, and a single-row peek carousel on
 *             mobile (cards peek the next via the `space-16`-gap track; no pagination dots).
 * Non-DS path — ProductListThree (existing non-PL layout, handles carousel/arrows/dots/card sizing).
 *              Reads product data from props.widgets (nested SINGLE_PRODUCT_CARD widgets).
 */
function ProductRecommendationsPdp(props: PLTypedWidgetProps<HorizontalProductListWidgetData>) {
  const data = props.data.horizontalProductListData
  const displayLimit = data?.displayLimit
  const isDS = shouldUseDesignSystem()
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  // While a swatch/color-change refetch is in flight the store keeps this widget visible (SWR) with
  // `isRefreshing` set. Mirror the non-PL similar-products section: overlay a spinner on the card
  // area (title stays put) so the user sees a loader while the request runs, instead of stale cards.
  // The store keys entries by `widget.id` (the internal UUID), NOT `widget.widgetId` (the API-request
  // key like "widget-pdp-hpl-…"). Looking up by widgetId silently returns undefined and the loader
  // never fires — see lazy-widget-fetcher.ts:141 for the id/widgetId split.
  const isRefreshing = useWidget(props.id)?.isRefreshing ?? false
  const loaderMinHeight = isDesktop ? '31rem' : '13.5rem'

  // "Shop Similar" (DS buy-box CTA) scrolls to `[data-section-name="similar-products-more-to-explore"]`.
  // All PDP reco bands (Shop the Look / More to Explore / Quince Shoppers Also Love / Based On Your Taste)
  // are the SAME widget type (HORIZONTAL_PRODUCT_LIST) with only data-driven titles — so target by the
  // widget's DB id, not type. Expose the anchor ONLY on the band whose id matches the `mte` ("More to
  // Explore") key in the `pl_widget_ids` Statsig config (a stable layout-slot id, BE-updatable, same
  // pattern as reviews_full). STRFRNT-15606.
  // "More to Explore" is a different widget/id per breakpoint — desktop renders it as this
  // HORIZONTAL_PRODUCT_LIST, mobile as PRODUCT_GRID_CAROUSEL — so read the DEVICE-SPECIFIC id from the
  // `pl_widget_ids` config: `mteDweb` on desktop, `mteMweb` on mobile. The Shop-Similar anchor lands on
  // whichever widget matches its device's id. STRFRNT-15606.
  // eslint-disable-next-line i18next/no-literal-string -- config keys, not user-facing copy
  const moreToExploreWidgetId = useStatsigPlWidgetId(isDesktop ? 'mteDweb' : 'mteMweb')
  const isMoreToExploreBand = matchesPlWidgetId(moreToExploreWidgetId, props.id, props.widgetId)

  // Star-rating visibility mirrors non-PL, which differs BY BAND (not uniform): "Shop the Look"
  // (pairWithProducts) hides ratings AND swatches; "More to Explore"/"Similar" (similarProducts) and
  // "Quince Shoppers Also Love" hide swatches but SHOW ratings. Swatches (`hideOptions`) are hidden on
  // every reco carousel in both flows. Identify the Shop-the-Look band via the device-specific
  // `stlDweb`/`stlMweb` pl_widget_ids keys — WEB-specific on purpose: the bare `stl` key in that
  // config carries the APP layout's widget id (the app has its own id space), same reason `mte`
  // (app) coexists with `mteDweb`/`mteMweb` (web). Hide ratings only on that band; every other HPL
  // reco shows them. STRFRNT-15585.
  // eslint-disable-next-line i18next/no-literal-string -- config keys, not user-facing copy
  const shopTheLookWidgetId = useStatsigPlWidgetId(isDesktop ? 'stlDweb' : 'stlMweb')
  const isShopTheLookBand = matchesPlWidgetId(shopTheLookWidgetId, props.id, props.widgetId)

  // Stylitics "Complete the Look" experiment: when ON, the Shop-the-Look band renders the Stylitics
  // Composer widget instead, with this band as its no-outfits fallback — the PL mirror of the non-PL
  // `CompleteTheLookWrapper` (Stylitics vs PairWithProducts) routing. Enabled only for the STL band
  // itself — this widget runs once per HPL band (MTE/FBT/Similar too), and an ungated hook would
  // exposure-log every band's render into the experiment population.
  const isStyliticsEnabled = useStyliticsExperiment(isShopTheLookBand)

  // "Quince Shoppers Also Love" / FBT band → non-PL `frequently_purchased_together` IS_VISIBLE. Same
  // BE-driven id resolution as stl/mte (device-specific `fbtDweb`/`fbtMweb` keys); inert until the
  // `pl_widget_ids` Statsig config carries the fbt id(s).
  // eslint-disable-next-line i18next/no-literal-string -- config keys, not user-facing copy
  const frequentlyBoughtTogetherWidgetId = useStatsigPlWidgetId(isDesktop ? 'fbtDweb' : 'fbtMweb')
  const isFrequentlyPurchasedTogetherBand = matchesPlWidgetId(
    frequentlyBoughtTogetherWidgetId,
    props.id,
    props.widgetId,
  )

  const allCards = props.renderedChildren ?? []
  const cards = displayLimit ? allCards.slice(0, displayLimit) : allCards

  // Reco telemetry (IA-only), shared with PRODUCT_GRID_CAROUSEL so both PDP reco containers stay in
  // lockstep: `ref` fires the per-band IS_VISIBLE (mapped bands only) + `recommendationView` on
  // scroll-into-view; `onCarouselArrowClick` fires CAROUSEL_LEFT/RIGHT_CLICK on arrow navigation.
  const {
    ref: recommendationViewRef,
    onCarouselArrowClick,
    cardProductViewTracker,
    cardClickTrackConfig,
  } = useRecoBandTelemetry({
    isShopTheLookBand,
    isMoreToExploreBand,
    isFrequentlyPurchasedTogetherBand,
    bandTitle: data?.title,
    widgetId: props.widgetId,
    productCount: cards.length,
  })

  // Desktop pages a 4-up row; the mobile peek track pages one card at a time.
  const slidesToScroll = isDesktop ? 4 : 1
  // Pre-gate only. The mobile branch owns 0-1199px (deviceType flips at `lg`) but the card is a
  // fixed 218px below `lg`, so how many fit scales with the viewport — four at the 768 frame. Embla's
  // measured snap list makes the real call.
  const { opts: carouselOpts, plugins: carouselPlugins } = useFreeDragCarousel({
    slidesToScroll,
    hasOverflow: cards.length > slidesToScroll,
  })

  // A 200 with no recommendations is a valid empty terminal state, not an error — render nothing
  // rather than throwing (which would trip the per-widget error boundary and emit Sentry noise).
  // (When the Stylitics routing below takes over the band, the null result doubles as "no fallback".)
  if (cards.length === 0) {
    console.warn(`[ProductRecommendationsPdp] No cards for widget ${props.widgetId}`)
  }

  // ── Non-DS path: pairWithProductsV2 + Swiper + renderedChildren ──────────
  // Widget-of-widget: renderedChildren = pre-rendered SingleProductCardWidget nodes.
  // Title rendered via pairWithProducts__title (matches existing PDP section style).
  const renderNonDsBand = (): React.ReactNode => {
    const maxWidth = getMaxWidthInPx({
      maxWidthDesktopInRem: pairStyles.productCardWidth ?? '29.8',
      maxWidthMobileInRem: pairStyles.productCardWidthMobile ?? '16',
      deviceType: deviceInfo.deviceType,
    })

    return (
      <CardTrackingProvider productViewTracker={cardProductViewTracker} productClickTrackConfig={cardClickTrackConfig}>
        <div
          ref={recommendationViewRef}
          className={pairStyles.pairWithProducts}
          // Only the "More to Explore" band (id === Statsig `mte`) carries the Shop-Similar scroll anchor.
          data-section-name={isMoreToExploreBand ? SIMILAR_PRODUCTS_SECTION_NAME_FOR_REF : undefined}
        >
          {data?.title ? (
            <h2
              className={classnames(
                pairStyles.pairWithProducts__title,
                pairStyles.pairWithProducts__title__newPdpDesignV2,
              )}
            >
              {data.title}
            </h2>
          ) : null}
          <LoaderWrapper showLoader={isRefreshing} minHeight={loaderMinHeight}>
            <Swiper
              // Stable key: keying by cards.length tore the whole Swiper down (destroy + re-init on
              // the main thread, mid-interaction) every time a dedupe pass trimmed the card set.
              key={props.id}
              className={pairStyles.swiperContainer}
              // `variableWidth` MUST stay true (matches non-PL pair-with-products-legacy line 312). The
              // base `.productCard` mixin sizes each card as `(container - 3*gutter)/4` and expects the
              // LAST card to drop its padding-right via `__lastEle` (applied below), so 3 cards + 3
              // gutters + 1 last card = container exactly. If we set variableWidth=false, Swiper locks
              // every slide to container/4 and the card's mixin width (which reserves only 3 gutters)
              // overflows the slide by ~10px per card — the last card gets cropped at the track's
              // clipped edge. Non-PL never hits this because it always uses variableWidth=true.
              variableWidth
              showNavigator={isDesktop}
              numberOfVisibleSlides={isDesktop ? 4 : 1}
              prevIconClass={pairStyles.swiperPrevIcon}
              nextIconClass={pairStyles.swiperNextIcon}
              arrowOnClick={({ direction }) => {
                onCarouselArrowClick(direction)
              }}
              id={`pdp-hpl-${props.widgetId}`}
            >
              {cards.map((child, index) => (
                <div
                  key={`${child.id || child.type}-${index}`}
                  // Base `.productCard` mixin reserves only 3 gutters for 4 cards; the LAST card MUST
                  // drop its 3.8rem padding-right (`__lastEle`) so the row sums to the container width
                  // exactly. Combined with `variableWidth={true}` on Swiper, this is a byte-for-byte
                  // mirror of non-PL pair-with-products-legacy (line 331). STRFRNT-15716.
                  className={classnames(
                    pairStyles.productCard,
                    index === cards.length - 1 && pairStyles.productCard__lastEle,
                  )}
                  style={{ maxWidth }}
                >
                  {/* Reco carousels hide swatches + ratings and use the taller 135% image aspect
                    (non-PL pairWithProducts / similarProducts parity — see
                    `$image-padding-height-recommendation-card`). */}
                  <CardDisplayOptionsProvider
                    hideOptions
                    hideReviews={isShopTheLookBand}
                    // Shop-the-Look uses the base 1.25 image box (matches non-PL pairWithProducts); the
                    // 1.35 recommendation aspect reserved ~10% extra blank space below the image, making
                    // the PL section ~39px taller than non-PL. STRFRNT-15804.
                    useRecommendationImageAspect={false}
                  >
                    {child.node}
                  </CardDisplayOptionsProvider>
                </div>
              ))}
            </Swiper>
          </LoaderWrapper>
        </div>
      </CardTrackingProvider>
    )
  }

  // ── DS path: DS Carousel ─────────────────────────────────────────────────
  const title = data?.title
  const hasMore = cards.length > 1

  // QA locator root, keyed off the Statsig band id (never the CMS title). Unmapped bands share the
  // generic RECOMMENDATIONS id — disambiguate via DOM order. The root doubles as the numbered
  // per-card SECTION_PRODUCT prefix (e.g. 'shop-the-look-products-01').
  const sectionTestId = isShopTheLookBand
    ? TEST_LOCATORS.PDP_SECTIONS.SHOP_THE_LOOK
    : isMoreToExploreBand
      ? TEST_LOCATORS.PDP_SECTIONS.MORE_TO_EXPLORE
      : isFrequentlyPurchasedTogetherBand
        ? TEST_LOCATORS.PDP_SECTIONS.FREQUENTLY_PURCHASED_TOGETHER
        : TEST_LOCATORS.PDP_SECTIONS.RECOMMENDATIONS

  const renderDsBand = (): React.ReactNode => (
    // Shop-Similar scroll anchor only on the "More to Explore" band (id === Statsig `mte`). STRFRNT-15606.
    <CardTrackingProvider productViewTracker={cardProductViewTracker} productClickTrackConfig={cardClickTrackConfig}>
      <section
        ref={recommendationViewRef}
        className={sectionClass}
        data-section-name={isMoreToExploreBand ? SIMILAR_PRODUCTS_SECTION_NAME_FOR_REF : undefined}
        data-testid={sectionTestId}
      >
        <Carousel opts={carouselOpts} plugins={carouselPlugins}>
          <div className={carouselWrapperClass}>
            <div className={headerRowClass}>
              {title ? <div className={headingClass}>{title}</div> : <div />}
              {isDesktop && hasMore ? (
                <div className={navGroupClass}>
                  <CarouselPreviousButton
                    onClick={() => {
                      onCarouselArrowClick(CAROUSEL_ARROW_DIRECTION.PREV)
                    }}
                  />
                  <CarouselNextButton
                    onClick={() => {
                      onCarouselArrowClick(CAROUSEL_ARROW_DIRECTION.NEXT)
                    }}
                  />
                </div>
              ) : null}
            </div>
            <LoaderWrapper showLoader={isRefreshing} minHeight={loaderMinHeight}>
              {/* `viewportCss` pairs with the edge-card margins in `carouselWrapperClass`: the inset
                  without this bleed would leave the first card 32px from the screen edge. */}
              <CarouselContent viewportCss={carouselViewportCss}>
                {cards.map((child, index) => (
                  <CarouselItem
                    key={`${child.id || child.type}-${index}`}
                    data-testid={TEST_LOCATORS.SECTION_PRODUCT(sectionTestId, index + 1)}
                  >
                    {/* Reco carousels hide swatches + ratings and use the taller 135% image aspect
                      (non-PL parity — see `$image-padding-height-recommendation-card`). */}
                    <CardDisplayOptionsProvider
                      hideOptions
                      hideReviews={isShopTheLookBand}
                      // Shop-the-Look uses the base 1.25 image box (matches non-PL pairWithProducts); the
                      // 1.35 recommendation aspect reserved ~10% extra blank space below the image, making
                      // the PL section ~39px taller than non-PL. STRFRNT-15804.
                      useRecommendationImageAspect={false}
                    >
                      {child.node}
                    </CardDisplayOptionsProvider>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </LoaderWrapper>
          </div>
        </Carousel>
      </section>
    </CardTrackingProvider>
  )

  const bandNode = cards.length === 0 ? null : isDS ? renderDsBand() : renderNonDsBand()

  // Shop-the-Look band + Stylitics experiment ON → render the Stylitics Composer widget, with this
  // band as its fallback when Stylitics resolves with no outfits (or fails to load). Rendered even
  // when the CTL reco came back empty — Stylitics can still have outfits for the product.
  if (isShopTheLookBand && isStyliticsEnabled) {
    return <StyliticsRecommendationPl fallback={bandNode} />
  }

  return bandNode
}

const carouselPlaceholder = <div style={{ height: '58rem', width: '100%' }} />

// 58rem legacy ≈ 36rem DS: legacy's fluid root font-size is scoped to `:not([data-styles='panda'])`,
// so a rem is ~10px on legacy and 16px under the panda scope — same ~580px reserved band either way.
const DS_PLACEHOLDER_HEIGHT = '36rem'

/**
 * Lazy-widget fallback. Must be a COMPONENT, not a module-scope element: `shouldUseDesignSystem()`
 * reads per-request state that `_app` seeds during its render pass, so at module-eval time it
 * answers `false` on the server (the module is imported before any `_app` render) and `true` in the
 * browser (it falls back to `_document`'s `<html data-styles="panda">` stamp). Branching at import
 * time therefore ships the legacy placeholder from SSR and the DS one on hydration — a mismatch
 * across the whole fallback subtree. Deciding inside render keeps both passes on one branch.
 */
function CarouselSuspenseFallback() {
  if (!shouldUseDesignSystem()) {
    return carouselPlaceholder
  }

  // DS fills the reserved band with a centered spinner instead of legacy's blank space.
  // `SvgLoaderWrapper` is the DS/legacy-forking wrapper, so it needs no branch of its own.
  return (
    <div style={{ height: DS_PLACEHOLDER_HEIGHT, width: '100%' }}>
      <SvgLoaderWrapper
        showLoader
        minHeight={DS_PLACEHOLDER_HEIGHT}
        loadWrapperClassName={loaderWrapperClass}
        loaderSize="2.75rem"
      />
    </div>
  )
}

export const ProductRecommendationsPdpWidget: PLTypedWidgetInterface<
  HorizontalProductListWidgetData,
  WidgetType.HORIZONTAL_PRODUCT_LIST
> = {
  id: WidgetType.HORIZONTAL_PRODUCT_LIST,
  errorBoundary: PLBaseErrorBoundary,
  render: ProductRecommendationsPdp,
  suspenseFallback: <CarouselSuspenseFallback />,
  // No cards → render() returns null. Tell the renderer up front so it emits no shell (no empty
  // `<div data-widget-type>`), keeping the PDP section-divider adjacency exact.
  // KNOWN EDGE: this also skips the Stylitics takeover of the Shop-the-Look band when the CTL reco
  // returns 0 cards (isEmpty is hook-free, so it can't read the experiment) — Stylitics-only outfits
  // are dropped in that case, same net behavior as the band itself being absent.
  isEmpty: props => (props.renderedChildren?.length ?? 0) === 0,
}
